const AUTH_STORAGE_KEY = "precept_auth_user";
const SESSION_STORAGE_KEY = "precept_auth_session";
const COOKIE_CHUNK_SIZE = 3000; // safe encoded value size per cookie (4096 limit minus name + attributes overhead)

const clone = (value) => {
  if (value == null) return null;

  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    console.error("Failed to clone auth payload:", error);
    return value;
  }
};

const isSecure = () => window.location.protocol === "https:";

const readCookieAuth = () => {
  try {
    const cookies = document.cookie.split("; ");

      // Read chunked cookies: precept_auth_user_0, precept_auth_user_1, ...
    const chunks = [];
    let index = 0;
    while (true) {
      const key = `${AUTH_STORAGE_KEY}_${index}`;
      const match = cookies.find((row) => row.startsWith(key + "="));
      if (!match) break;
      chunks.push(decodeURIComponent(match.split("=").slice(1).join("=")));
      index++;
    }
    if (chunks.length > 0) {
      return JSON.parse(chunks.join(""));
    }

    // Fallback: read legacy single cookie
    const match = cookies.find((row) => row.startsWith(AUTH_STORAGE_KEY + "="));
    if (!match) return null;
    return JSON.parse(decodeURIComponent(match.split("=").slice(1).join("=")));
  } catch (error) {
    console.error("Error reading auth cookie:", error);
    return null;
  }
};

const writeCookieAuth = (user) => {
  const nextValue = JSON.stringify(user);
  const current = readCookieAuth();
  if (JSON.stringify(current) === nextValue) return false;

  const secure = isSecure() ? "; Secure" : "";
  const encoded = encodeURIComponent(nextValue);

  clearCookieAuth();

  const totalChunks = Math.ceil(encoded.length / COOKIE_CHUNK_SIZE);
  for (let i = 0; i < totalChunks; i++) {
    const chunk = encoded.slice(i * COOKIE_CHUNK_SIZE, (i + 1) * COOKIE_CHUNK_SIZE);
    document.cookie = `${AUTH_STORAGE_KEY}_${i}=${chunk}; path=/; max-age=2592000; SameSite=Strict${secure}`;
  }

  return true;
};

const clearCookieAuth = () => {
  // Clear legacy single cookie
  document.cookie = `${AUTH_STORAGE_KEY}=; path=/; max-age=0; SameSite=Strict`;
  // Clear all chunk cookies
  document.cookie.split("; ").forEach((cookie) => {
    const name = cookie.split("=")[0];
    if (name.startsWith(`${AUTH_STORAGE_KEY}_`)) {
      document.cookie = `${name}=; path=/; max-age=0; SameSite=Strict`;
    }
  });
};

const readSessionCookieAuth = () => {
  try {
    const cookies = document.cookie.split("; ");
    const chunks = [];
    let index = 0;
    while (true) {
      const key = `${SESSION_STORAGE_KEY}_${index}`;
      const match = cookies.find((row) => row.startsWith(key + "="));
      if (!match) break;
      chunks.push(decodeURIComponent(match.split("=").slice(1).join("=")));
      index++;
    }
    if (chunks.length > 0) {
      return JSON.parse(chunks.join(""));
    }
    return null;
  } catch (error) {
    console.error("Error reading session auth cookie:", error);
    return null;
  }
};

const writeSessionCookieAuth = (user) => {
  const nextValue = JSON.stringify(user);
  const current = readSessionCookieAuth();
  if (JSON.stringify(current) === nextValue) return;

  const secure = isSecure() ? "; Secure" : "";
  const encoded = encodeURIComponent(nextValue);

  clearSessionCookieAuth();

  const totalChunks = Math.ceil(encoded.length / COOKIE_CHUNK_SIZE);
  for (let i = 0; i < totalChunks; i++) {
    const chunk = encoded.slice(i * COOKIE_CHUNK_SIZE, (i + 1) * COOKIE_CHUNK_SIZE);
    document.cookie = `${SESSION_STORAGE_KEY}_${i}=${chunk}; path=/; SameSite=Strict${secure}`;
  }
};

export const clearSessionCookieAuth = () => {
  document.cookie.split("; ").forEach((cookie) => {
    const name = cookie.split("=")[0];
    if (name.startsWith(`${SESSION_STORAGE_KEY}_`)) {
      document.cookie = `${name}=; path=/; max-age=0; SameSite=Strict`;
    }
  });
};

export const authChannel =
  typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel("auth_sync")
    : null;

const readPersistentAuth = () => readCookieAuth();

const writePersistentAuth = (user) => {
  const changed = writeCookieAuth(user);
  if (changed) {
    authChannel?.postMessage({ type: "update", user });
  }
};

const clearPersistentAuth = () => {
  const hadPersistedAuth = readCookieAuth() !== null;
  clearCookieAuth();
  if (hadPersistedAuth) {
    authChannel?.postMessage({ type: "clear" });
  }
};

export const storeAuthTokens = (user) => {
  if (!user) return;

  const nextUser = clone(user);

  if (nextUser?.keepLoggedIn === true) {
    writePersistentAuth(nextUser);
    clearSessionCookieAuth();
    return;
  }

  writeSessionCookieAuth(nextUser);
  clearPersistentAuth();
};

export const getAuthTokens = () => {
  const sessionUser = readSessionCookieAuth();
  if (sessionUser?.idToken && sessionUser?.accessToken) {
    return clone(sessionUser);
  }

  const persistentUser = readPersistentAuth();
  if (persistentUser?.idToken && persistentUser?.accessToken) {
    return persistentUser;
  }

  return null;
};

export const clearAuthTokens = () => {
  clearPersistentAuth();
  clearSessionCookieAuth();
};

// Clears auth state without broadcasting — use when responding to a logout
// event from another tab to avoid re-broadcasting and creating a loop.
export const clearAuthSilent = () => {
  clearCookieAuth();
  clearSessionCookieAuth();
};

export const isAuthenticated = () => {
  const tokens = getAuthTokens();
  return !!(tokens?.idToken && tokens?.accessToken);
};

export const isAuthPersisted = () => {
  return !!readPersistentAuth();
};

export { AUTH_STORAGE_KEY };
